import { useState } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Activity } from 'lucide-react'
import { isLocalRunUrl, safeExternalUrl, stageStatusLabel } from '@/utils/patchStage'
import { formatDateTime } from '@/utils/format'
import type { InstallProgress, PatchStage, PatchTargetResult, RunnerStageStep } from '@/types/patching'
import styles from './Patching.module.css'

const STEP_ORDER = ['validate', 'inventory', 'preflight', 'install', 'verify', 'accept']

const TONE: Record<string, string> = {
  succeeded: styles.badgeDone,
  failed: styles.badgeFailed,
  skipped: styles.badgePending,
  running: styles.badgeActive,
}

function stepRows(steps: Record<string, RunnerStageStep>): [string, RunnerStageStep][] {
  return Object.entries(steps).sort((a, b) => {
    const ai = STEP_ORDER.indexOf(a[0])
    const bi = STEP_ORDER.indexOf(b[0])
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
    return (a[1].at ?? '').localeCompare(b[1].at ?? '')
  })
}

function outcomeLabel(result: PatchTargetResult): string {
  if (result.dry_run) return 'Dry run — nothing was installed'
  if (result.status === 'succeeded') return 'Patched successfully'
  if (result.status === 'failed') return 'Patch failed'
  if (result.status === 'skipped') return 'Not installed'
  return result.status || 'Unknown'
}

function isStaleDryRun(stage: PatchStage, result: PatchTargetResult): boolean {
  if (stage.dry_run) return false
  const text = `${result.error ?? ''} ${result.skipped_reason ?? ''}`.toLowerCase()
  return text.includes('dry run') || text.includes('was not called')
}

function currentResults(stage: PatchStage): PatchTargetResult[] {
  return (stage.results ?? []).filter((result) => !isStaleDryRun(stage, result))
}

function installHeadline(stage: PatchStage): string {
  const results = currentResults(stage)
  const dryRun = results.some((result) => result.dry_run)
  const install = stage.runner_stages?.install
  if (stage.status === 'awaiting_approval') return 'Waiting for approval. Patch installation has not started.'
  if (stage.status === 'approved' && dryRun) return 'Approved. The last run was a dry run, so no patches were installed.'
  if (stage.status === 'approved' && results.length > 0) return 'Approved. The last run finished. Each VM below says whether it was patched.'
  if (stage.status === 'approved') return 'Approved. Execute this environment to start installation.'
  if (stage.status === 'execute_dispatched' || stage.status === 'executing' || stage.status === 'validating') {
    return install?.message || 'Installation is in progress. This page refreshes while the run is active.'
  }
  if (stage.status === 'accepted') return 'This environment was accepted after installation.'
  if (stage.status === 'failed') return stage.failure?.reason || 'The run failed before installation could finish.'
  return stageStatusLabel(stage.status)
}

function RunLocation({ stage }: { stage: PatchStage }) {
  const external = isLocalRunUrl(stage.run_url) ? null : safeExternalUrl(stage.run_url)
  if (external) {
    return (
      <p className={styles.muted} data-testid="pipeline-note">
        GitHub Actions pipeline:{' '}
        <a href={external} target="_blank" rel="noopener noreferrer" data-testid="pipeline-link">Open pipeline</a>
      </p>
    )
  }
  if (isLocalRunUrl(stage.run_url) || stage.run_id) {
    return (
      <p className={styles.muted} data-testid="local-run-note">
        This install runs in Platform Central through Azure Update Manager. The dev site does not start a GitHub Actions workflow.
        {' '}
        <Link to="?tab=report" data-testid="report-link">Open the run report</Link>
      </p>
    )
  }
  return null
}

function installPercent(progress: InstallProgress): number {
  const selected = progress.installed + progress.pending + progress.failed
  if (progress.phase === 'succeeded') return 100
  if (selected <= 0) return progress.phase === 'finalizing' ? 90 : 8
  const raw = Math.round((progress.installed / selected) * 100)
  if (progress.phase === 'finalizing' || progress.pending === 0) return Math.max(Math.min(raw, 100), 90)
  return Math.min(raw, 99)
}

function progressSentence(progress: InstallProgress): string {
  const selected = progress.installed + progress.pending + progress.failed
  if (progress.phase === 'succeeded') return `Azure finished. ${progress.installed} installed, ${progress.failed} failed.`
  if (progress.phase === 'failed') return `${progress.failed} failed, ${progress.installed} installed, ${progress.pending} pending.`
  if (progress.pending > 0) {
    return `${progress.installed} of ${selected} selected updates installed, ${progress.pending} still pending.`
  }
  return `${progress.installed} selected updates installed. Azure is finishing the operation.`
}

function InstallBar({ stage }: { stage: PatchStage }) {
  const running = stage.status === 'execute_dispatched' || stage.status === 'executing' || stage.status === 'validating'
  const progress = stage.runner_stages?.install?.progress
  if (!running && !progress) return null
  if (!progress) {
    return (
      <div data-testid="install-progress" data-phase="waiting">
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-label="Patch install progress"
          aria-valuetext="Waiting for Azure to report installed and pending counts"
        >
          <div className={styles.progressIndeterminate} />
        </div>
        <p className={styles.cellSub}>Waiting for Azure to report how many updates are installed and how many are still pending.</p>
      </div>
    )
  }
  const percent = installPercent(progress)
  const since = stage.started_at || progress.started_at
  return (
    <div data-testid="install-progress" data-phase={progress.phase}>
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-label="Patch install progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={progressSentence(progress)}
      >
        <div className={styles.progressFill} style={{ width: `${percent}%` }} />
      </div>
      <p className={styles.progressMeta} data-testid="install-counts">
        <span>{progress.installed} installed</span>
        <span>{progress.pending} pending</span>
        <span>{progress.failed} failed</span>
        <span>{progress.not_selected} not selected</span>
      </p>
      {since ? <p className={styles.cellSub}>Installing since {formatDateTime(since)}</p> : null}
      {progress.pending > 0 ? (
        <p className={styles.cellSub}>Azure has not named which selected update is still pending.</p>
      ) : null}
    </div>
  )
}

function statusBody(stage: PatchStage): string {
  const progress = stage.runner_stages?.install?.progress
  const lines = [installHeadline(stage), '']
  if (progress) {
    lines.push(`${progress.installed} installed, ${progress.pending} pending, ${progress.failed} failed, ${progress.not_selected} not selected.`)
    lines.push('')
  }
  for (const assessment of stage.assessments ?? []) {
    for (const patch of assessment.patches ?? []) {
      const number = patch.kb_or_package || 'unnamed update'
      const where = patch.state === 'not selected'
        ? 'Not selected for this install'
        : patch.state === 'installed'
          ? 'Installed'
          : patch.state === 'in progress'
            ? 'In progress'
            : 'Included in this install'
      lines.push(`${number} — ${patch.name} (${patch.classification}) — ${where}`)
    }
  }
  if (lines.length === 2) lines.push('Individual patch names are not on this campaign yet.')
  const checks = stage.post_validation ?? []
  if (checks.length > 0) {
    lines.push('', 'Post-patch validation:')
    for (const check of checks) {
      lines.push(`${check.check}: ${check.status} — ${check.detail}`)
    }
  }
  lines.push('', 'Sent from Platform Central.')
  return lines.join('\n')
}

function PatchStatusEmail({ stage }: { stage: PatchStage }) {
  const [to, setTo] = useState('')
  const subject = `Patch status ${stage.environment.toUpperCase()}`
  function send() {
    const recipient = to.trim()
    const href = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(statusBody(stage))}`
    window.location.href = href
  }
  return (
    <form
      className={styles.actionRow}
      data-testid="patch-email"
      onSubmit={(event) => {
        event.preventDefault()
        send()
      }}
    >
      <label className={styles.muted}>
        Customer email{' '}
        <input
          type="email"
          value={to}
          placeholder="customer@company.com"
          onChange={(event) => setTo(event.target.value)}
          data-testid="patch-email-to"
        />
      </label>
      <button type="submit" className={styles.chip}>Email this status</button>
      <span className={styles.cellSub}>Opens your email with the patch list. This site does not send mail itself.</span>
    </form>
  )
}

/** Plan and install steps for this environment, including a per-VM patched / failed / not-installed result. */
export function RunProgress({ stage }: { stage: PatchStage }) {
  const steps = stepRows(stage.runner_stages ?? {})
  const results = currentResults(stage)
  const running = stage.status === 'execute_dispatched' || stage.status === 'executing' || stage.status === 'validating' || stage.status === 'plan_running'
  return (
    <section className={styles.section} id="run-progress" aria-label="Installation progress" data-testid="run-progress">
      <h3 className={styles.sectionTitle}>
        <Activity size={14} /> Installation progress
      </h3>
      <p className={styles.muted} data-testid="install-headline">{installHeadline(stage)}</p>
      <InstallBar stage={stage} />
      <RunLocation stage={stage} />
      <PatchStatusEmail stage={stage} />
      {stage.run_id && <p className={styles.muted}>Run <span className={styles.mono}>{stage.run_id}</span></p>}
      {running && steps.length === 0 && (
        <p className={styles.muted} data-testid="progress-waiting">Waiting for the first status update from the installer.</p>
      )}
      {steps.length > 0 && (
        <ul className={styles.checkList} data-testid="runner-steps">
          {steps.map(([name, step]) => (
            <li key={name}>
              <span className={clsx(styles.badge, TONE[step.status] ?? styles.badgePending)}>{step.status || 'recorded'}</span>{' '}
              <span className={styles.mono}>{name}</span>
              {step.message ? ` — ${step.message}` : ''}
            </li>
          ))}
        </ul>
      )}
      {results.length > 0 && (
        <ul className={styles.checkList} data-testid="install-outcomes">
          {results.map((result) => (
            <li key={result.vm_name} data-vm={result.vm_name} data-install={result.status}>
              <strong>{result.vm_name}</strong> — {outcomeLabel(result)}
              {!result.dry_run && result.error ? `. ${result.error}` : ''}
              {!result.dry_run && result.skipped_reason ? ` (${result.skipped_reason})` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
